import { apiPost } from "./api";

export interface ConsoleEvent {
  event: string;
  data?: unknown;
  sound?: boolean;
}

// Open the shop-scoped Console socket. Exchanges the Bearer token for a
// single-use ticket (keeps the token out of the ws URL), connects, and
// reconnects with capped exponential backoff. Returns a disposer.
export function connectConsoleWs(onEvent: (e: ConsoleEvent) => void): () => void {
  let socket: WebSocket | null = null;
  let closed = false;
  let retry = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const scheduleReopen = () => {
    if (closed) return;
    retry = Math.min(retry + 1, 6);
    timer = setTimeout(open, 1000 * Math.pow(2, retry - 1));
  };

  async function open(): Promise<void> {
    if (closed) return;
    try {
      const { ticket } = await apiPost<{ ticket: string }>("/ws-ticket");
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${proto}//${window.location.host}/api/v1/console/ws?ticket=${encodeURIComponent(ticket)}`;
      socket = new WebSocket(url);
      socket.onopen = () => {
        retry = 0;
      };
      socket.onmessage = (m) => {
        try {
          onEvent(JSON.parse(m.data) as ConsoleEvent);
        } catch {
          /* ignore non-JSON frames */
        }
      };
      socket.onclose = () => {
        if (!closed) scheduleReopen();
      };
      socket.onerror = () => {
        try {
          socket?.close();
        } catch {
          /* noop */
        }
      };
    } catch {
      scheduleReopen();
    }
  }

  void open();

  return () => {
    closed = true;
    if (timer) clearTimeout(timer);
    try {
      socket?.close();
    } catch {
      /* noop */
    }
  };
}
